import { getFormSchema } from 'forms';
import {
  categoryToVistaMapping,
  vistaToRubricMapping,
  ORGANIZATION_CATEGORIES,
  INDIVIDUAL_CATEGORIES,
} from 'constants/evaluation';

// Merge judgment data with submissions
export const mergeJudgmentData = (submissions, judgments, aspects, rubrics) => {
  // Create lookups for faster access
  const aspectsLookup = aspects.reduce((acc, aspect) => {
    acc[aspect.documentId] = aspect;
    return acc;
  }, {});

  // Group judgments by submission_document_id for multi-judge scoring
  const judgmentsBySubmission = judgments.reduce((acc, judgment) => {
    if (!acc[judgment.submission_document_id]) {
      acc[judgment.submission_document_id] = [];
    }
    acc[judgment.submission_document_id].push(judgment);
    return acc;
  }, {});

  // Removed excessive debug logs

  return submissions.map(submission => {
    const submissionJudgments =
      judgmentsBySubmission[submission.documentId] || [];

    // Processing submission silently

    if (submissionJudgments.length === 0) {
      return {
        ...submission,
        judgment_data: null,
        aspect_scores: {},
        total_score: null,
        judge_count: 0,
        judged_at: null,
        judge_name: null,
        overall_summary: null,
        all_judgments: [],
      };
    }

    const vistaCategoryName = categoryToVistaMapping(submission.category);

    // Find the correct rubric for this submission's category
    const categoryRubric = rubrics.find(rubric => {
      if (!rubric.name) return false;

      const expectedRubricName = vistaToRubricMapping(vistaCategoryName);
      return expectedRubricName && rubric.name === expectedRubricName;
    });

    // Rubric lookup completed

    // Create a lookup for aspect weights from the rubric (aspect_id -> weight)
    const aspectWeights = {};
    if (categoryRubric && categoryRubric.aspect_list) {
      categoryRubric.aspect_list.forEach(aspectItem => {
        aspectWeights[aspectItem.aspect_id] = aspectItem.weight; // Keep as percentage
      });
      // Aspect weights loaded from rubric
    } else {
      // Using equal weights fallback
    }

    // Calculate weighted scores for each judge
    const allJuryWeightedScores = submissionJudgments.map(
      (judgment, _index) => {
        if (!judgment.aspect_scores || judgment.aspect_scores.length === 0) {
          return { judgment, weighted_score: 0 };
        }

        let totalWeightedScore = 0;
        let totalWeight = 0;
        const numberOfAspects = judgment.aspect_scores.length;
        const equalWeight = 100 / numberOfAspects; // Fallback equal weight

        judgment.aspect_scores.forEach((aspectScore, _aspectIndex) => {
          // Try to get weight from rubric first, fallback to equal weight
          const weight = aspectWeights[aspectScore.aspect_id] || equalWeight;
          const score = aspectScore.value || 0;
          const weightedContribution = score * (weight / 100); // Convert percentage to decimal

          totalWeightedScore += weightedContribution;
          totalWeight += weight / 100; // Convert percentage to decimal
        });

        // Calculate final weighted score (should be out of 10 if scores are 1-10)
        const finalWeightedScore = totalWeight > 0 ? totalWeightedScore : 0;

        return {
          judgment,
          weighted_score: finalWeightedScore,
          total_weighted_score: totalWeightedScore,
          total_weight: totalWeight,
        };
      }
    );

    // Calculate final submission score as average of all judge weighted scores
    const validWeightedScores = allJuryWeightedScores.filter(
      result => result.weighted_score > 0
    );
    const finalSubmissionScore =
      validWeightedScores.length > 0
        ? validWeightedScores.reduce(
            (sum, result) => sum + result.weighted_score,
            0
          ) / validWeightedScores.length
        : null;

    // Final score calculation completed

    // Use the latest judgment for legacy fields, but keep all judgment data
    const latestJudgment = submissionJudgments[0];

    // Calculate aspect scores for display (using latest judgment as primary)
    const aspectScores = {};
    if (latestJudgment.aspect_scores) {
      latestJudgment.aspect_scores.forEach(aspectScore => {
        const aspect = aspectsLookup[aspectScore.aspect_id];
        if (aspect) {
          aspectScores[aspect.name] = {
            score: aspectScore.value,
            comment: aspectScore.comment,
            aspect_id: aspectScore.aspect_id,
          };
        }
      });
    }

    return {
      ...submission,
      judgment_data: latestJudgment,
      aspect_scores: aspectScores,
      total_score: finalSubmissionScore,
      judge_count: submissionJudgments.length,
      judged_at: latestJudgment.judged_at,
      judge_name: latestJudgment.judge_name,
      overall_summary: latestJudgment.overall_summary,
      // Enhanced all_judgments with calculated weighted scores
      all_judgments: submissionJudgments.map((judgment, index) => {
        const judgeResult = allJuryWeightedScores[index];
        return {
          judge_name: judgment.judge_name,
          judge_email: judgment.judge_email,
          judged_at: judgment.judged_at,
          overall_summary: judgment.overall_summary,
          weighted_total_score: judgeResult.weighted_score, // Calculated weighted score
          total_weighted_score: judgeResult.total_weighted_score, // Raw weighted sum
          total_weight: judgeResult.total_weight, // Total weight used
          aspect_scores: judgment.aspect_scores.map(aspectScore => {
            const aspect = aspectsLookup[aspectScore.aspect_id];
            const weight =
              aspectWeights[aspectScore.aspect_id] ||
              100 / judgment.aspect_scores.length;
            return {
              aspect_id: aspectScore.aspect_id,
              aspect_name: aspect ? aspect.name : aspectScore.aspect_id,
              score: aspectScore.value,
              value: aspectScore.value,
              comment: aspectScore.comment,
              weight: weight, // Include weight for detailed breakdown
            };
          }),
        };
      }),
    };
  });
};

// Transform Strapi data to match our table structure
export const transformStrapiDataToTableFormat = strapiData => {
  if (!strapiData || !Array.isArray(strapiData) || strapiData.length === 0) {
    return [];
  }

  const schema = getFormSchema('smdd');

  return strapiData
    .map((item, index) => {
      if (!item) {
        return null;
      }

      const category = item.category || 'Unknown';
      const fieldMapping = schema.fieldMappings[category] || {};
      const categoryFields = schema.fieldsByCategory[category] || [];

      const getFieldLabel = fieldName => {
        const field = categoryFields.find(f => f.name === fieldName);
        return field
          ? field.label
          : fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };

      const transformedFields: Record<string, any> = {};
      Object.entries(fieldMapping).forEach(([schemaField, strapiField]) => {
        const fieldName = strapiField as string;
        if (item[fieldName] !== null && item[fieldName] !== undefined) {
          transformedFields[schemaField] = {
            value: item[fieldName],
            label: getFieldLabel(schemaField),
            strapiField: fieldName,
          };
        }
      });

      const getMainDescription = () => {
        if (category === 'AI Breakthrough Award') {
          return (
            transformedFields.ai_project_name?.value ||
            item.description ||
            'No AI project name available'
          );
        }
        if (ORGANIZATION_CATEGORIES.includes(category)) {
          return (
            transformedFields.project_name?.value ||
            item.description ||
            'No project name available'
          );
        }
        if (INDIVIDUAL_CATEGORIES.includes(category)) {
          return (
            transformedFields.leadership_achievement?.value ||
            item.description ||
            'No achievement description available'
          );
        }
        return item.description || 'No description available';
      };

      const detailed_fields: Record<string, any> = {};
      const excludeFromDetailed = [
        'submission_type',
        'pillar',
        'organization_unit',
        'full_name',
        'title',
        'email',
        'phone',
      ];

      Object.entries(transformedFields).forEach(([fieldName, fieldData]) => {
        if (!excludeFromDetailed.includes(fieldName) && fieldData.value) {
          detailed_fields[fieldData.label] = fieldData.value;
        }
      });

      const additionalFields = {
        'Award Material': item.award_material,
        'Video URL': item.video_url,
        'Team Members': item.team_members,
        Screenshots: item.screenshots,
      };

      Object.entries(additionalFields).forEach(([label, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          detailed_fields[label] = value;
        }
      });

      return {
        id: item.id || index + 1,
        documentId: item.documentId,
        submission_type: item.submission_type || 'organization',
        full_name: item.full_name || 'Unknown',
        pillar: item.pillar || 'Unknown',
        organization_unit: item.org_unit || 'Unknown',
        title: item.title || 'Unknown',
        email: item.email || 'Unknown',
        phone: item.phone || '',
        category: category,
        project_name: getMainDescription(),
        leadership_achievement: item.description,
        submitted_date: item.createdAt || new Date().toISOString(),
        status: item.status || 'Pending Review',
        competition_stage: item.competition_stage || null,
        judge1_score: item.judge1_score || null,
        judge2_score: item.judge2_score || null,
        judge3_score: item.judge3_score || null,
        total_score: item.total_score || null,
        detailed_fields: detailed_fields,
        stage_action_logs: item.stage_action_logs || null,
        _original: item,
      };
    })
    .filter(Boolean);
};
